import React, { useEffect, useCallback } from 'react';
import { getColumns } from './utils/columns';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { PageContainer, GridContainer } from '../IdCards/styled';
import { dataGridSx } from '../common/styled/DataGrid';
import pallet from '../../layouts/pallet';
import Loader from '../../components/Loader/Loader';
import ToastMessage from '../../components/ToastMessage';
import SearchFilters from './components/SearchFilters';
import PromotionDialog from './components/PromotionDialog';
import RolloverActions from './components/RolloverActions';
import { useSchoolClassesConfigHook } from '../common/hooks/useSchoolClassesConfig';
import useProvisionalPromotion from './utils/useProvisionalPromotion';
import usePromotionDialogForm from './utils/usePromotionDialogForm';
import { getUniqueGrades } from '../../utils/gradeUtils';
import { PaperWrapper, Title } from '../common/styled';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import CustomDataGridToolbar from '../common/components/CustomDataGridToolbar';

const ProvisionalPromotionPage: React.FC = () => {
  const defaultAcademicYear = '2027-2028';
  const { gradeOptions, divisionOptions, schoolClassesConfig } =
    useSchoolClassesConfigHook();
  const grades: string[] = Array.isArray(schoolClassesConfig?.grades)
    ? (schoolClassesConfig!.grades as string[])
    : [];
  const uniqueGrades = getUniqueGrades(grades);
  const filteredGrades = uniqueGrades.map((grade) => ({
    label: grade,
    value: grade
  }));

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const {
    grade,
    division,
    students,
    selectedIds,
    hasSearched,
    loading,
    toast,
    allRowsSelected,
    isSomeRowsSelected,
    setToast,
    handleSearch,
    onHeaderCheckboxClick,
    onCellCheckboxClick,
    onGradeChange,
    onDivisionChange
  } = useProvisionalPromotion(defaultAcademicYear);

  const {
    dialogOpen,
    submitting,
    formValues,
    formErrors,
    fixedConcessions,
    openDialog,
    closeDialog,
    handleFieldChange,
    handleDialogSubmit,
    fetchFixedConcessions
  } = usePromotionDialogForm(selectedIds, setToast);

  const handleSubmitAndRefresh = useCallback(async () => {
    const prevSelectedCount = selectedIds.length;
    await handleDialogSubmit();
    if (prevSelectedCount > 0) {
      handleSearch();
    }
  }, [handleDialogSubmit, handleSearch, selectedIds.length]);

  useEffect(() => {
    fetchFixedConcessions(loggedInUser.school);
    // eslint-disable-next-line
  }, []);

  const columns: GridColDef[] = getColumns({
    selectedIds,
    onHeaderCheckboxClick,
    onCellCheckboxClick,
    allRowsSelected,
    isSomeRowsSelected
  }) as GridColDef[];

  return (
    <PageContainer>
      <Title>Provisional Promotion</Title>
      <SearchFilters
        grade={grade}
        division={division}
        gradeOptions={gradeOptions}
        divisionOptions={divisionOptions}
        loading={loading}
        onGradeChange={onGradeChange}
        onDivisionChange={onDivisionChange}
        onSearch={handleSearch}
      />
      <Loader show={loading} />
      {hasSearched && !loading && (
        <PaperWrapper sx={{ flexDirection: 'column', gap: 2 }}>
          <RolloverActions
            rolloverAy={defaultAcademicYear}
            selectedCount={selectedIds.length}
            onPromoteClick={openDialog}
          />
          <GridContainer>
            <DataGrid
              autoHeight
              rows={students}
              columns={columns}
              getRowId={(row) => row.id}
              checkboxSelection={false}
              showToolbar
              slots={{ toolbar: CustomDataGridToolbar }}
              slotProps={{
                toolbar: {
                  printOptions: { disableToolbarButton: true },
                  showQuickFilter: true
                }
              }}
              disableRowSelectionOnClick
              loading={loading}
              sx={dataGridSx(pallet)}
            />
          </GridContainer>
        </PaperWrapper>
      )}
      <PromotionDialog
        open={dialogOpen}
        submitting={submitting}
        formValues={formValues as any}
        formErrors={formErrors}
        gradeOptions={filteredGrades}
        divisionOptions={divisionOptions}
        fixedConcessions={fixedConcessions}
        onClose={closeDialog}
        onSubmit={handleSubmitAndRefresh}
        onFieldChange={handleFieldChange}
      />
      <ToastMessage
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast({ ...toast, open: false })}
      />
    </PageContainer>
  );
};

export default ProvisionalPromotionPage;
